import { useState, useEffect } from "react";
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Truck, ClipboardList,
  Menu, X, ChevronLeft, ChevronRight, Building2,
  Navigation, BadgeCheck, Settings,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { getImageUrl } from "../../utils/getImageUrl";

const NAV = [
  { label: "Despacho",     icon: Navigation,      to: "/delivery/dispatch",     end: true },
  { label: "Mis Ryders",   icon: Truck,           to: "/delivery/ryders" },
  { label: "Solicitudes",  icon: ClipboardList,   to: "/delivery/applications", badge: "applications" },
  { label: "Dashboard",    icon: LayoutDashboard, to: "/delivery/dashboard" },
  { label: "Mi empresa",   icon: Settings,        to: "/delivery/profile" },
];

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

export default function DeliveryLayout() {
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [collapsed, setCollapsed]         = useState(false);
  const [pendingApps, setPendingApps]     = useState(0);
  const [company, setCompany]             = useState(null);
  const [_companyChecked, setCompanyChecked] = useState(false);

  const stored  = getStoredUser();
  const isAuth  = Boolean(stored && stored.role === "delivery");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuth) return;
    API.get("/delivery-companies/me")
      .then(r => {
        const comp = r.data?.data?.company ?? r.data?.company ?? null;
        setCompany(comp);
        setCompanyChecked(true);
        if (!comp && location.pathname !== "/delivery/profile") {
          navigate("/delivery/profile", { replace: true });
        }
      })
      .catch(() => {
        setCompanyChecked(true);
        if (location.pathname !== "/delivery/profile") {
          navigate("/delivery/profile", { replace: true });
        }
      });
  }, [isAuth]); // eslint-disable-line

  useEffect(() => {
    if (!company?.id) return;
    const poll = () =>
      API.get(`/delivery-companies/${company.id}/applications?status=pendiente&type=application`)
        .then(r => setPendingApps(r.data?.data?.total ?? r.data?.total ?? 0))
        .catch(() => {});
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, [company?.id]);

  if (!isAuth) return <Navigate to="/login" replace />;

  const logoSrc = company?.logo ? getImageUrl(company.logo) : null;

  function SidebarInner({ onNav }) {
    return (
      <div className="flex flex-col h-full">
        <div className={`flex items-center gap-3 px-3 py-4 border-b border-gray-100 min-h-[64px] ${collapsed ? "justify-center" : ""}`}>
          {logoSrc ? (
            <img src={logoSrc} alt={company?.name} className="w-9 h-9 rounded-xl object-cover border border-gray-200 shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Building2 size={18} className="text-indigo-600" />
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{company?.name ?? "Empresa de domicilio"}</p>
                {company?.verified && <BadgeCheck size={13} className="text-blue-500 shrink-0" />}
              </div>
              <p className="text-[10px] text-gray-400 leading-tight">Panel de despacho</p>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {NAV.map(({ label, icon: Icon, to, badge, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onNav}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium transition-all group ${
                  isActive
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} className={`shrink-0 ${isActive ? "text-indigo-600" : "text-gray-500 group-hover:text-gray-700"}`} />
                  {!collapsed && <span className="flex-1 truncate">{label}</span>}
                  {badge === "applications" && pendingApps > 0 && (
                    <span className={`font-bold text-white bg-red-500 rounded-full flex items-center justify-center ${
                      collapsed
                        ? "absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px]"
                        : "min-w-[20px] h-5 px-1 text-[10px] shrink-0"
                    }`}>
                      {pendingApps > 99 ? "99+" : pendingApps}
                    </span>
                  )}
                  {collapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                      {label}
                    </div>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-2 border-t border-gray-100">
          <button
            onClick={() => setCollapsed(v => !v)}
            className="hidden lg:flex w-full items-center gap-3 px-2.5 py-2 rounded-xl text-xs font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
            style={{ justifyContent: collapsed ? "center" : "flex-start" }}
          >
            {collapsed ? <ChevronRight size={15} /> : <><ChevronLeft size={15} /><span>Colapsar</span></>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50" style={{ minHeight: "calc(100vh - 56px)" }}>
      <aside
        className="hidden lg:block shrink-0 bg-white border-r border-gray-200 transition-all duration-200"
        style={{ width: collapsed ? 64 : 224 }}
      >
        <div className="sticky top-14 overflow-y-auto" style={{ height: "calc(100vh - 56px)" }}>
          <SidebarInner onNav={() => {}} />
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 bottom-0 z-50 w-64 bg-white border-r border-gray-200 shadow-xl flex flex-col lg:hidden transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between px-4 h-14 border-b border-gray-100 shrink-0">
          <span className="font-bold text-gray-800 text-sm">Menú</span>
          <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-hidden">
          <SidebarInner onNav={() => setMobileOpen(false)} />
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden flex items-center gap-3 px-4 h-12 bg-white border-b border-gray-200 shrink-0 sticky top-14 z-30">
          <button onClick={() => setMobileOpen(true)} className="p-1.5 -ml-1 rounded-lg hover:bg-gray-100 text-gray-600">
            <Menu size={20} />
          </button>
          <Building2 size={17} className="text-indigo-600 shrink-0" />
          <span className="font-bold text-gray-800 text-sm truncate">{company?.name ?? "Empresa de domicilio"}</span>
          {pendingApps > 0 && (
            <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
              {pendingApps}
            </span>
          )}
        </div>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
